import React from 'react';
import cn from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import profileBgM from 'src/assets/profile/profile-bg-m.png';
import Avatar from 'src/components/common/Avatar';
import Controls from 'src/components/common/Controls';
import Icon from 'src/components/common/Icon';
import { ROOT } from 'src/constants/routes';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { signOut } from 'src/store/user/actions';
import { selectUser } from 'src/store/user/selectors';
import { getTotalPrayerHubsLabel } from 'src/utils/get-total-prayer-hubs-label';

import css from './Profile.module.scss';
import { RankProgress } from './RankProgress';
import { useRating } from './helpers';

// const SAVED_CAMPAIGNS = demoData.slice(0, 4);

export default function Profile() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const animatedSparks = useCountUp({ to: user.points, durationMs: 700 });
  const { rank, amountToNextLevel } = useRating(user.points);
  const currentRank = `שלב נוכחי: ${rank}`;
  const toNextRankLeft = `עוד ${amountToNextLevel} ניצוצות לדרגה הבאה`;
  const totalPrayers = `${user.totalPrayers} תפילות`;
  const totalCampaigns = getTotalPrayerHubsLabel(user.totalPrayerDuration);

  // const savedTitle = 'המוקדים שהתפללתי עבורם';
  const onLogout = () => {
    dispatch(signOut());
  };

  return (
    <div className={css.profile} style={{ backgroundImage: `url(${profileBgM})` }}>
      <header className={css.header}>
        <button type="button" className={css.logoutBtn} onClick={onLogout}>
          התנתק/י
        </button>
        <div className={cn(css.backBtn, css.bubbleShadow)} onClick={() => navigate(-1)}>
          <Icon type="arrow-left" />
        </div>
      </header>

      <Controls />

      <div className={css.content}>
        <div className={cn(css.general, css.bubbleShadow)}>
          <Avatar size="large" linked={false} avatarUrl={user.avatar} className={css.avatar} />

          <div className={css.main}>
            <h6 className={css.userName}>{user.name}</h6>
            <p className={css.score}>
              <Icon type="star-four-corners" className={css.scoreStar} />
              <span>{animatedSparks} ניצוצות</span>
            </p>
          </div>

          <div className={cn(css.rank, css.bubbleShadow)}>
            <p className={css.rankTitle}>{currentRank}</p>
            <p className={css.rankSubtitle}>{toNextRankLeft}</p>
            <RankProgress className={css.rankProgress} rank={user.rank} />
          </div>

          <div className={css.statistics}>
            <div className={cn(css.statCard, css.bubbleShadow)}>
              <Icon type="users-big" className={css.statIconUsers} />
              <p>{totalCampaigns}</p>
            </div>
            <div className={cn(css.statCard, css.bubbleShadow)}>
              <Icon type="praying-hands-big" />
              <p>{totalPrayers}</p>
            </div>
          </div>
        </div>

        {/* <section className={css.savedSection} aria-labelledby="profile-saved-title">
          <h2 id="profile-saved-title" className={css.savedTitle}>
            {savedTitle}
          </h2>
          <div className={css.savedList}>
            {SAVED_CAMPAIGNS.map(campaign => (
              <Campaign key={campaign._id} />
            ))}
          </div>
        </section> */}
      </div>
    </div>
  );
}

function useCountUp({ to, durationMs }: { to: number; durationMs: number }) {
  const [value, setValue] = React.useState(0);
  const rafRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const start = performance.now();
    const from = 0;
    const delta = to - from;

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const next = Math.round(from + delta * t);
      setValue(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    setValue(0);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [durationMs, to]);

  return value;
}
